import { useCallback, useEffect, useMemo, useState } from 'react';
import * as aiChatApi from '../services/aiChat';

const PENDING_POLL_MS = 3000;

export function useAiChat({ request, showStatus, workspaceId }) {
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [busy, setBusy] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [progressStep, setProgressStep] = useState(null);

    const loadSessions = useCallback(async (preferredId) => {
        if (!workspaceId) return;
        setSessionsLoading(true);
        try {
            const list = await aiChatApi.listSessions(request, workspaceId);
            setSessions(list || []);
            const nextActive = preferredId || (list && list[0] && list[0].id) || null;
            setActiveSessionId(nextActive);
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось загрузить чаты');
        } finally {
            setSessionsLoading(false);
        }
    }, [request, showStatus, workspaceId]);

    useEffect(() => {
        setActiveSessionId(null);
        setMessages([]);
        if (workspaceId) {
            loadSessions();
        } else {
            setSessions([]);
        }
    }, [workspaceId, loadSessions]);

    const loadMessages = useCallback(async (sessionId) => {
        if (!sessionId) {
            setMessages([]);
            return;
        }
        try {
            const list = await aiChatApi.listMessages(request, sessionId);
            setMessages(list || []);
            return list || [];
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось загрузить сообщения');
            return null;
        }
    }, [request, showStatus]);

    // Ответ агента, который ещё пишется на сервере (status=pending). Бывает после
    // обновления страницы или обрыва соединения: агент продолжает работу,
    // а чат ждёт ответ опросом.
    const awaitingReply = useMemo(() => messages.some((m) => m.status === 'pending'), [messages]);
    const agentBusy = busy || awaitingReply;
    const visibleMessages = useMemo(() => messages.filter((m) => m.status !== 'pending'), [messages]);

    useEffect(() => {
        if (!awaitingReply || busy || !activeSessionId) return undefined;
        const timer = setInterval(() => {
            aiChatApi.listMessages(request, activeSessionId)
                .then((list) => setMessages(list || []))
                .catch(() => {
                    // best-effort, следующий тик попробует снова
                });
        }, PENDING_POLL_MS);
        return () => clearInterval(timer);
    }, [awaitingReply, busy, activeSessionId, request]);

    useEffect(() => {
        loadMessages(activeSessionId);
        setPendingConfirmation(null);
    }, [activeSessionId, loadMessages]);

    useEffect(() => {
        if (!agentBusy || !activeSessionId) {
            setProgressStep(null);
            return undefined;
        }
        let cancelled = false;
        const poll = async () => {
            try {
                const result = await aiChatApi.getProgress(request, activeSessionId);
                if (!cancelled) setProgressStep(result?.step || null);
            } catch {
                // progress polling is best-effort, ignore failures
            }
        };
        poll();
        const timer = setInterval(poll, 1000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [agentBusy, activeSessionId, request]);

    const handleCreateSession = useCallback(async () => {
        if (!workspaceId) return;
        try {
            const session = await aiChatApi.createSession(request, workspaceId, 'Новый чат');
            setSessions((prev) => [session, ...prev]);
            setActiveSessionId(session.id);
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось создать чат');
        }
    }, [request, showStatus, workspaceId]);

    const handleSelectSession = useCallback((sessionId) => {
        setActiveSessionId(sessionId);
    }, []);

    const handleDeleteSession = useCallback(async (sessionId) => {
        try {
            await aiChatApi.deleteSession(request, sessionId);
            setSessions((prev) => prev.filter((s) => s.id !== sessionId));
            if (activeSessionId === sessionId) {
                setActiveSessionId(null);
                setMessages([]);
            }
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось удалить чат');
        }
    }, [request, showStatus, activeSessionId]);

    const handleRenameSession = useCallback(async (sessionId, title) => {
        const trimmed = (title || '').trim();
        if (!trimmed) return;
        try {
            const updated = await aiChatApi.renameSession(request, sessionId, trimmed);
            setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title: updated.title } : s)));
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось переименовать чат');
        }
    }, [request, showStatus]);

    const handleClearMessages = useCallback(async () => {
        if (!activeSessionId) return;
        try {
            await aiChatApi.clearMessages(request, activeSessionId);
            setMessages([]);
            setPendingConfirmation(null);
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось очистить чат');
        }
    }, [request, showStatus, activeSessionId]);

    const handleInputChange = useCallback((event) => {
        setInputValue(event.target.value);
    }, []);

    const handleSend = useCallback(async () => {
        const content = inputValue.trim();
        if (!content || agentBusy) return;

        let sessionId = activeSessionId;
        if (!sessionId) {
            if (!workspaceId) return;
            try {
                const session = await aiChatApi.createSession(request, workspaceId, content.slice(0, 60));
                setSessions((prev) => [session, ...prev]);
                sessionId = session.id;
                setActiveSessionId(sessionId);
            } catch (error) {
                showStatus?.('error', error.message || 'Не удалось создать чат');
                return;
            }
        }

        setInputValue('');
        setBusy(true);
        setPendingConfirmation(null);

        const optimisticUserMessage = {
            id: `optimistic-${Date.now()}`,
            role: 'user',
            content,
            status: 'completed',
            created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, optimisticUserMessage]);

        try {
            const response = await aiChatApi.sendMessage(request, sessionId, content);
            setMessages((prev) => [
                ...prev.filter((m) => m.id !== optimisticUserMessage.id),
                response.user_message,
                response.assistant_message,
            ]);
            if (response.requires_confirmation) {
                setPendingConfirmation({
                    tool: response.pending_tool,
                    preview: response.pending_tool_preview,
                });
            }
            setSessions((prev) => {
                const updated = prev.map((s) => (s.id === sessionId ? { ...s, updated_at: new Date().toISOString() } : s));
                return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            });
        } catch (error) {
            // Соединение могло оборваться, пока агент работает: вопрос уже
            // сохранён на сервере, а ответ допишется — подтягиваем его опросом.
            const serverMessages = await loadMessages(sessionId);
            const stillAnswering = (serverMessages || []).some((m) => m.status === 'pending');
            if (!stillAnswering) {
                if (!serverMessages) {
                    setMessages((prev) => prev.filter((m) => m.id !== optimisticUserMessage.id));
                }
                showStatus?.('error', error.message || 'Не удалось отправить сообщение');
            }
        } finally {
            setBusy(false);
        }
    }, [inputValue, agentBusy, activeSessionId, workspaceId, request, showStatus, loadMessages]);

    const handleInputKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleMessageFeedback = useCallback(async (messageId, vote) => {
        if (!activeSessionId) return;
        try {
            const updated = await aiChatApi.sendMessageFeedback(request, activeSessionId, messageId, vote);
            setMessages((prev) => prev.map((m) => (m.id === messageId ? updated : m)));
        } catch (error) {
            showStatus?.('error', error.message || 'Не удалось отправить отзыв');
        }
    }, [request, showStatus, activeSessionId]);

    return {
        sessions,
        sessionsLoading,
        activeSessionId,
        messages: visibleMessages,
        inputValue,
        busy: agentBusy,
        progressStep,
        pendingConfirmation,
        onCreateSession: handleCreateSession,
        onSelectSession: handleSelectSession,
        onDeleteSession: handleDeleteSession,
        onRenameSession: handleRenameSession,
        onClearMessages: handleClearMessages,
        onInputChange: handleInputChange,
        onInputKeyDown: handleInputKeyDown,
        onSend: handleSend,
        onMessageFeedback: handleMessageFeedback,
    };
}
